import type { IHttpClient, IHttpError, RequestOptionsFactory } from '@wix/http-client'
import {
	createOnlineOrder,
	memberGetOrder,
	memberListOrders,
	requestCancellation,
} from '@wix/ambassador-pricing-plans-v2-order/http'
import type {
	CreateOnlineOrderResponse,
	MemberGetOrderResponse,
	MemberListOrdersResponse,
	Order,
} from '@wix/ambassador-pricing-plans-v2-order/types'
import { CancellationEffectiveAt } from '@wix/ambassador-pricing-plans-v2-order/types'
import { mapV2OrderToVeloOrder } from './mapV2OrderToVeloOrder'
import { PaidPlansError } from './paidPlansError'

export class PaidPlansV2Api {
	constructor(private readonly httpClient: IHttpClient) {}

	createOrder = async (planId: string) => {
		const data: CreateOnlineOrderResponse = await this.sendRequest(createOnlineOrder({ planId }))
		return {
			orderId: data.order?.id as string,
			wixPayOrderId: data.order?.wixPayOrderId ?? undefined,
		}
	}

	cancelOrder = async (orderId: string) => {
		const data: MemberGetOrderResponse = await this.sendRequest(memberGetOrder({ id: orderId }))
		await this.sendRequest(
			requestCancellation({
				id: orderId,
				effectiveAt: isRecurringOrder(data.order)
					? CancellationEffectiveAt.NEXT_PAYMENT_DATE
					: CancellationEffectiveAt.IMMEDIATELY,
			})
		)
	}

	getCurrentMemberOrders = async (limit = 50, offset = 0) => {
		const data: MemberListOrdersResponse = await this.sendRequest(memberListOrders({ limit, offset }))
		return (data.orders ?? []).map(mapV2OrderToVeloOrder)
	}

	private async sendRequest<T>(requestFactory: RequestOptionsFactory<T>): Promise<T> {
		try {
			const response = await this.httpClient.request(withArtifactHeader(requestFactory))
			return response.data
		} catch (e) {
			throw toPaidPlansError(e)
		}
	}
}

const isHttpError = (payload: any): payload is IHttpError => {
	return !!payload?.isWixHttpError
}

const toPaidPlansError = (e: any) => {
	if (isHttpError(e)) {
		if (e.response?.status && e.response?.data.message) {
			return new PaidPlansError(e.response.status, e.response?.data.message)
		} else {
			return new PaidPlansError(e.response?.status ?? 418, e.message)
		}
	} else {
		return new PaidPlansError(e.status ?? 418, e.message)
	}
}

const withArtifactHeader = <T>(requestFactory: RequestOptionsFactory<T>): RequestOptionsFactory<T> => {
	return (context) => {
		const requestOptions = requestFactory(context)
		return {
			...requestOptions,
			headers: {
				...requestOptions.headers,
				'X-Wix-Client-Artifact-Id': 'feature-paid-plans-wix-code-sdk',
			},
		}
	}
}

const isRecurringOrder = (order: Order | undefined) =>
	!!(order?.pricing?.subscription ?? order?.priceDetails?.subscription)
